import { useState } from 'react'
import { SimulationState } from '@/types/simulation'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  BarChart3,
  TrendingDown,
  TrendingUp,
  DollarSign,
  Users,
  Percent,
  ArrowRight,
  AlertCircle,
  Download,
  X
} from 'lucide-react'
import { format } from 'date-fns'

interface SimulationComparisonProps {
  simulations: SimulationState[]
  open: boolean
  onOpenChange: (open: boolean) => void
}

export default function SimulationComparison({
  simulations,
  open,
  onOpenChange
}: SimulationComparisonProps) {
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>([
    'total_shares',
    'valuation',
    'dilution',
    'cash_flow'
  ])

  // Extract comparison data from simulations
  const getComparisonData = () => {
    const metrics = {
      total_shares: {
        name: 'Total Shares',
        icon: BarChart3,
        values: simulations.map(sim => ({
          scenario: sim.name,
          before: sim.results?.cap_table_before.total_shares || 0,
          after: sim.results?.cap_table_after.total_shares || 0,
          change: (sim.results?.cap_table_after.total_shares || 0) - 
                  (sim.results?.cap_table_before.total_shares || 0),
          changePercent: ((sim.results?.cap_table_after.total_shares || 0) - 
                          (sim.results?.cap_table_before.total_shares || 0)) / 
                         (sim.results?.cap_table_before.total_shares || 1) * 100
        }))
      },
      valuation: {
        name: 'Valuation',
        icon: DollarSign,
        values: simulations.map(sim => ({
          scenario: sim.name,
          before: sim.results?.cap_table_before.valuation || 0,
          after: sim.results?.cap_table_after.valuation || 0,
          change: (sim.results?.cap_table_after.valuation || 0) - 
                  (sim.results?.cap_table_before.valuation || 0),
          changePercent: ((sim.results?.cap_table_after.valuation || 0) - 
                          (sim.results?.cap_table_before.valuation || 0)) / 
                         (sim.results?.cap_table_before.valuation || 1) * 100
        }))
      },
      dilution: {
        name: 'Max Dilution',
        icon: TrendingDown,
        values: simulations.map(sim => {
          const maxDilution = Math.max(
            ...(sim.results?.dilution_impact?.map(d => d.dilution_percentage) || [0])
          )
          return {
            scenario: sim.name,
            value: maxDilution,
            affected: sim.results?.dilution_impact?.filter(d => d.dilution_percentage > 0).length || 0
          }
        })
      },
      cash_flow: {
        name: 'Cash Impact',
        icon: DollarSign,
        values: simulations.map(sim => ({
          scenario: sim.name,
          inflow: sim.results?.financial_impact?.cash_inflow || 0,
          outflow: sim.results?.financial_impact?.cash_outflow || 0,
          net: (sim.results?.financial_impact?.cash_inflow || 0) - 
               (sim.results?.financial_impact?.cash_outflow || 0)
        }))
      }
    }

    return metrics
  }

  const comparisonData = getComparisonData()

  // Get unique shareholders across all simulations
  const getAllShareholders = () => {
    const shareholderMap = new Map<string, string>()
    
    simulations.forEach(sim => {
      sim.results?.cap_table_before.shareholders.forEach(sh => {
        shareholderMap.set(sh.user_id, sh.name)
      })
      sim.results?.cap_table_after.shareholders.forEach(sh => {
        shareholderMap.set(sh.user_id, sh.name)
      })
    })

    return Array.from(shareholderMap.entries()).map(([id, name]) => ({ id, name }))
  }

  const shareholders = getAllShareholders()

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value)
  }

  // Format number
  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(Math.round(value))
  }

  // Get scenario type badge color
  const getScenarioColor = (type: string) => {
    switch (type) {
      case 'new_hire': return 'bg-blue-100 text-blue-800'
      case 'funding_round': return 'bg-green-100 text-green-800'
      case 'exit': return 'bg-purple-100 text-purple-800'
      case 'milestone': return 'bg-yellow-100 text-yellow-800'
      case 'option_exercise': return 'bg-orange-100 text-orange-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const exportComparison = () => {
    // Create CSV export
    const headers = ['Metric', ...simulations.map(s => s.name)]
    const rows = [
      headers,
      ['Scenario Type', ...simulations.map(s => s.scenario_type)],
      ['Created', ...simulations.map(s => format(new Date(s.created_at), 'yyyy-MM-dd'))],
      [''],
      ['Total Shares Before', ...simulations.map(s => s.results?.cap_table_before.total_shares || 0)],
      ['Total Shares After', ...simulations.map(s => s.results?.cap_table_after.total_shares || 0)],
      ['Share Change', ...simulations.map(s => 
        (s.results?.cap_table_after.total_shares || 0) - (s.results?.cap_table_before.total_shares || 0)
      )],
      [''],
      ['Valuation Before', ...simulations.map(s => s.results?.cap_table_before.valuation || 0)],
      ['Valuation After', ...simulations.map(s => s.results?.cap_table_after.valuation || 0)],
      ['Valuation Change', ...simulations.map(s => 
        (s.results?.cap_table_after.valuation || 0) - (s.results?.cap_table_before.valuation || 0)
      )],
      [''],
      ['Max Dilution %', ...simulations.map(s => 
        Math.max(...(s.results?.dilution_impact?.map(d => d.dilution_percentage) || [0]))
      )],
      ['Cash Inflow', ...simulations.map(s => s.results?.financial_impact?.cash_inflow || 0)],
      ['Cash Outflow', ...simulations.map(s => s.results?.financial_impact?.cash_outflow || 0)],
    ]

    const csv = rows.map(row => row.join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `simulation-comparison-${format(new Date(), 'yyyy-MM-dd')}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh]">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle>Scenario Comparison</DialogTitle>
              <DialogDescription>
                Compare {simulations.length} simulation scenarios side by side
              </DialogDescription>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={exportComparison}
              >
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
            </div>
          </div>
        </DialogHeader>

        <ScrollArea className="h-[calc(90vh-120px)]">
          <div className="space-y-6 pr-4">
            {/* Scenario Overview */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {simulations.map((sim) => (
                <Card key={sim.id}>
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-base">{sim.name}</CardTitle>
                        <p className="text-sm text-gray-500 mt-1">
                          {format(new Date(sim.created_at), 'MMM d, yyyy')}
                        </p>
                      </div>
                      <Badge className={getScenarioColor(sim.scenario_type)}>
                        {sim.scenario_type.replace('_', ' ')}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600">{sim.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Key Metrics Comparison */}
            <Card>
              <CardHeader>
                <CardTitle>Key Metrics</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Total Shares */}
                  <div>
                    <h4 className="font-medium mb-3 flex items-center">
                      <BarChart3 className="h-4 w-4 mr-2" />
                      Total Shares
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {comparisonData.total_shares.values.map((val, idx) => (
                        <div key={idx} className="bg-gray-50 rounded-lg p-3">
                          <p className="text-sm text-gray-600 mb-1">{val.scenario}</p>
                          <div className="flex items-center gap-2">
                            <span className="text-lg font-semibold">
                              {formatNumber(val.before)}
                            </span>
                            <ArrowRight className="h-4 w-4 text-gray-400" />
                            <span className="text-lg font-semibold">
                              {formatNumber(val.after)}
                            </span>
                          </div>
                          <p className={`text-sm mt-1 ${val.change > 0 ? 'text-green-600' : 'text-gray-500'}`}>
                            {val.change > 0 ? '+' : ''}{formatNumber(val.change)} ({val.changePercent.toFixed(1)}%)
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Valuation */}
                  <div>
                    <h4 className="font-medium mb-3 flex items-center">
                      <DollarSign className="h-4 w-4 mr-2" />
                      Valuation
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {comparisonData.valuation.values.map((val, idx) => (
                        <div key={idx} className="bg-gray-50 rounded-lg p-3">
                          <p className="text-sm text-gray-600 mb-1">{val.scenario}</p>
                          <div className="flex items-center gap-2">
                            <span className="text-lg font-semibold">
                              {formatCurrency(val.before)}
                            </span>
                            <ArrowRight className="h-4 w-4 text-gray-400" />
                            <span className="text-lg font-semibold">
                              {formatCurrency(val.after)}
                            </span>
                          </div>
                          <p className={`text-sm mt-1 ${val.change > 0 ? 'text-green-600' : val.change < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                            {val.change > 0 ? '+' : ''}{formatCurrency(val.change)} ({val.changePercent.toFixed(1)}%)
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Max Dilution */}
                  <div>
                    <h4 className="font-medium mb-3 flex items-center">
                      <TrendingDown className="h-4 w-4 mr-2" />
                      Maximum Dilution
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {comparisonData.dilution.values.map((val, idx) => (
                        <div key={idx} className="bg-gray-50 rounded-lg p-3">
                          <p className="text-sm text-gray-600 mb-1">{val.scenario}</p>
                          <p className="text-2xl font-semibold text-red-600">
                            {val.value.toFixed(2)}%
                          </p>
                          <p className="text-sm text-gray-500">
                            {val.affected} shareholders affected
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Cash Flow */}
                  <div>
                    <h4 className="font-medium mb-3 flex items-center">
                      <DollarSign className="h-4 w-4 mr-2" />
                      Cash Impact
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {comparisonData.cash_flow.values.map((val, idx) => (
                        <div key={idx} className="bg-gray-50 rounded-lg p-3">
                          <p className="text-sm text-gray-600 mb-1">{val.scenario}</p>
                          <div className="space-y-1">
                            {val.inflow > 0 && (
                              <p className="text-sm">
                                <span className="text-gray-500">In:</span>{' '}
                                <span className="text-green-600 font-medium">
                                  +{formatCurrency(val.inflow)}
                                </span>
                              </p>
                            )}
                            {val.outflow > 0 && (
                              <p className="text-sm">
                                <span className="text-gray-500">Out:</span>{' '}
                                <span className="text-red-600 font-medium">
                                  -{formatCurrency(val.outflow)}
                                </span>
                              </p>
                            )}
                            <p className="text-base font-semibold pt-1 border-t">
                              Net: {val.net >= 0 ? '+' : ''}{formatCurrency(val.net)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Shareholder Impact Table */}
            <Card>
              <CardHeader>
                <CardTitle>Shareholder Impact</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Shareholder</TableHead>
                        {simulations.map((sim) => (
                          <TableHead key={sim.id} className="text-center" colSpan={2}>
                            {sim.name}
                          </TableHead>
                        ))}
                      </TableRow>
                      <TableRow>
                        <TableHead></TableHead>
                        {simulations.map((sim) => (
                          <>
                            <TableHead key={`${sim.id}-shares`} className="text-center">
                              Shares
                            </TableHead>
                            <TableHead key={`${sim.id}-percent`} className="text-center">
                              %
                            </TableHead>
                          </>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {shareholders.slice(0, 10).map((shareholder) => (
                        <TableRow key={shareholder.id}>
                          <TableCell className="font-medium">{shareholder.name}</TableCell>
                          {simulations.map((sim) => {
                            const before = sim.results?.cap_table_before.shareholders.find(
                              s => s.user_id === shareholder.id
                            )
                            const after = sim.results?.cap_table_after.shareholders.find(
                              s => s.user_id === shareholder.id
                            )
                            
                            return (
                              <>
                                <TableCell key={`${sim.id}-${shareholder.id}-shares`} className="text-center">
                                  {after ? (
                                    <div>
                                      <span>{formatNumber(after.shares)}</span>
                                      {before && after.shares !== before.shares && (
                                        <span className={`text-xs block ${after.shares > before.shares ? 'text-green-600' : 'text-red-600'}`}>
                                          {after.shares > before.shares ? '+' : ''}{formatNumber(after.shares - before.shares)}
                                        </span>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </TableCell>
                                <TableCell key={`${sim.id}-${shareholder.id}-percent`} className="text-center">
                                  {after ? (
                                    <div>
                                      <span>{after.percentage.toFixed(2)}%</span>
                                      {before && Math.abs(after.percentage - before.percentage) > 0.01 && (
                                        <span className={`text-xs block ${after.percentage > before.percentage ? 'text-green-600' : 'text-red-600'}`}>
                                          {after.percentage > before.percentage ? '+' : ''}{(after.percentage - before.percentage).toFixed(2)}%
                                        </span>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </TableCell>
                              </>
                            )
                          })}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {shareholders.length > 10 && (
                    <p className="text-sm text-gray-500 text-center mt-2">
                      Showing top 10 shareholders. {shareholders.length - 10} more not shown.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Warnings Summary */}
            {simulations.some(s => s.results?.warnings && s.results.warnings.length > 0) && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <AlertCircle className="h-5 w-5 mr-2 text-yellow-600" />
                    Warnings
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {simulations.map((sim) => {
                      if (!sim.results?.warnings || sim.results.warnings.length === 0) return null
                      return (
                        <div key={sim.id}>
                          <p className="font-medium text-sm mb-1">{sim.name}</p>
                          <ul className="list-disc list-inside text-sm text-yellow-700 space-y-1">
                            {sim.results.warnings.map((warning, idx) => (
                              <li key={idx}>{warning}</li>
                            ))}
                          </ul>
                        </div>
                      )
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  )
}